/**
 * React Query hooks for Contact Requests API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contactRequestsApi, CreateContactRequestDto, ContactRequest } from '@/services/api/contact-requests.api';

export function useMyContactRequests() {
  return useQuery<ContactRequest[], Error>({
    queryKey: ['contact-requests', 'my'],
    queryFn: () => contactRequestsApi.getMyRequests(),
    staleTime: 60 * 1000,
  });
}

export function useContactRequestsByDeal(dealId: string) {
  return useQuery<ContactRequest[], Error>({
    queryKey: ['contact-requests', 'deal', dealId],
    queryFn: () => contactRequestsApi.getByDeal(dealId),
    enabled: !!dealId,
    staleTime: 60 * 1000,
  });
}

export function useCreateContactRequest() {
  const queryClient = useQueryClient();
  return useMutation<ContactRequest, Error, CreateContactRequestDto>({
    mutationFn: (dto) => contactRequestsApi.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-requests'] });
    },
  });
}

export function useAcceptContactRequest() {
  const queryClient = useQueryClient();
  return useMutation<ContactRequest, Error, string>({
    mutationFn: (id) => contactRequestsApi.accept(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-requests'] });
    },
  });
}

export function useRejectContactRequest() {
  const queryClient = useQueryClient();
  return useMutation<ContactRequest, Error, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => contactRequestsApi.reject(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-requests'] });
    },
  });
}

export function useAllContactRequests() {
  return useQuery<ContactRequest[], Error>({
    queryKey: ['contact-requests', 'all'],
    queryFn: () => contactRequestsApi.getAll(),
    staleTime: 60 * 1000,
  });
}

export function useReceivedContactRequests() {
  return useQuery<ContactRequest[], Error>({
    queryKey: ['contact-requests', 'received'],
    queryFn: () => contactRequestsApi.getReceived(),
    staleTime: 60 * 1000,
  });
}
